import { Component, h, Prop, State, Event, EventEmitter, Element, Listen, Watch } from '@stencil/core';
import { CheckboxOption, CheckboxGroupDirection, ThemeConfig } from '@nylas/core';

/**
 * The `checkbox-group` component is a UI component that allows users to select multiple options from a list.
 * It manages a group of checkboxes and their states.
 */
@Component({
  tag: 'checkbox-group',
  styleUrl: 'checkbox-group.scss',
  shadow: true,
})
export class CheckboxGroup {
  @Element() el!: HTMLElement;
  private readonly componentType: string = 'checkbox-group';

  /**
   * The name of the checkbox group. This is used to identify the group when submitting a form.
   */
  @Prop() name: string = 'checkbox-group';

  /**
   * The label for the checkbox group.
   */
  @Prop() label: string = '';

  /**
   * Array of options to display as checkboxes.
   * Each option should have a label and value.
   */
  @Prop() options: CheckboxOption[] = [];

  /**
   * Whether the checkbox group is required. If true, at least one checkbox must be checked when submitting a form.
   */
  @Prop() required: boolean = false;

  /**
   * The layout direction of the checkboxes.
   * 'vertical' - checkboxes are stacked vertically (default)
   * 'horizontal' - checkboxes are arranged horizontally
   */
  @Prop() direction: CheckboxGroupDirection = 'vertical';

  /**
   * The theme config for the checkbox group.
   */
  @Prop() themeConfig?: ThemeConfig;

  /**
   * The error message state.
   */
  @State() error: string = '';

  @State() selectedValues: string[] = [];

  /**
   * This event is fired when any checkbox in the group is toggled.
   */
  @Event() nylasCheckboxGroupChanged!: EventEmitter<{
    selectedValues: string[];
    name: string;
  }>;

  // Lifecycle events
  componentWillLoad() {
    this.el.setAttribute('component-type', this.componentType);
  }

  componentDidLoad() {
    this.selectedValues = this.options.filter(option => option.checked).map(option => option.value);
  }

  @Watch('options')
  optionsChangedHandler(newValue: CheckboxOption[], oldValue: CheckboxOption[]) {
    if (JSON.stringify(newValue) === JSON.stringify(oldValue)) return;
    this.selectedValues = newValue.filter(option => option.checked).map(option => option.value);
  }

  @Watch('themeConfig')
  themeConfigChangedHandler(newVal: ThemeConfig, oldVal: ThemeConfig) {
    if (newVal === oldVal) return;
    this.applyThemeConfig(newVal);
  }

  applyThemeConfig(themeConfig?: ThemeConfig) {
    if (themeConfig) {
      for (const [key, value] of Object.entries(themeConfig)) {
        this.el.style.setProperty(`${key}`, value);
      }
    }
  }

  connectedCallback() {
    // Use queueMicrotask to defer theme application until after React has set props.
    // This fixes the timing issue where connectedCallback fires before React sets themeConfig.
    queueMicrotask(() => {
      if (this.themeConfig) {
        this.applyThemeConfig(this.themeConfig);
        this.el.setAttribute('data-themed', '');
      }
    });
  }

  // Event listeners
  @Listen('bookingFormSubmitted', { target: 'document' })
  handleBookingFormSubmitted(event: CustomEvent) {
    if (this.required && !this.hasCheckedOptions()) {
      this.error = 'Please select at least one option.';
    }
    if (this.error) {
      event.preventDefault();
    }
  }

  // Methods
  private hasCheckedOptions(): boolean {
    return this.options.some(option => option.checked);
  }

  handleCheckboxToggle(event: CustomEvent) {
    const { checked, name: _checkboxName, label } = event.detail;

    // Find the option that was toggled
    const toggledOption = this.options.find(option => option.label === label);
    if (!toggledOption) return;

    // Update selectedValues based on the checked state
    let selected: string[] = [];
    if (checked) {
      if (toggledOption.selectAll) {
        selected = this.options.map(option => option.value);
      } else {
        selected = [...this.selectedValues, toggledOption.value];
      }
    } else {
      selected = this.selectedValues.filter(value => value !== toggledOption.value);
    }
    this.selectedValues = [...selected];

    // Emit the change event
    this.nylasCheckboxGroupChanged.emit({
      selectedValues: this.selectedValues,
      name: this.name,
    });
  }

  @Listen('nylasFormCheckboxToggled', { target: 'document' })
  handleCheckboxToggled(event: CustomEvent) {
    this.handleCheckboxToggle(event);
  }

  render() {
    return (
      <div class="checkbox-group-container">
        {this.label && (
          <div class="group-label">
            {this.label}
            {this.required && <span class="required">*</span>}
          </div>
        )}
        <div class={`checkbox-list ${this.direction}`}>
          {this.options.map(option => (
            <checkbox-component
              name={`${this.name}-${option.value}`}
              label={option.label}
              checked={this.selectedValues.includes(option.value)}
              readOnly={option.disabled}
              required={this.required}
              themeConfig={this.themeConfig}
            />
          ))}
        </div>
        {this.error && <p class="error">{this.error}</p>}
      </div>
    );
  }
}
